import type { PhotoStore, StoredPhoto } from '../../application/ports.ts'
import type { PhotoId } from '../../domain/ids.ts'
import type { MisionHogarDb } from './db.ts'

/**
 * Photos live outside the event log, as blobs, and the log only ever holds their
 * id. Three things matter here.
 *
 * They are downscaled before they are stored, which keeps a tablet from filling
 * up over a school year and, as a side effect, drops the EXIF block: a photo of
 * a tidy bedroom has no business carrying the coordinates of the house.
 *
 * They are kept only as long as they are useful. A "this is how it looks when it
 * is done" reference photo stays until the task is deleted; a photo taken to
 * prove today's mission is gone a fortnight later.
 */

const MAX_EDGE = 1024
const QUALITY = 0.72

async function downscale(blob: Blob): Promise<Blob> {
  if (!blob.type.startsWith('image/')) return blob
  try {
    const bitmap = await createImageBitmap(blob)
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height))
    const width = Math.round(bitmap.width * scale)
    const height = Math.round(bitmap.height * scale)

    const canvas = new OffscreenCanvas(width, height)
    const context = canvas.getContext('2d')
    if (!context) {
      bitmap.close()
      return blob
    }
    context.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()
    const reduced = await canvas.convertToBlob({ type: 'image/jpeg', quality: QUALITY })
    // A tiny original beats a re-encoded copy that came out larger.
    return reduced.size < blob.size ? reduced : blob
  } catch {
    // A browser without OffscreenCanvas still gets to keep its photo.
    return blob
  }
}

export class DexiePhotoStore implements PhotoStore {
  private readonly db: MisionHogarDb
  private readonly newId: () => string

  constructor(db: MisionHogarDb, newId: () => string) {
    this.db = db
    this.newId = newId
  }

  async put(blob: Blob, kind: StoredPhoto['kind']): Promise<PhotoId> {
    const reduced = await downscale(blob)
    const id = this.newId() as PhotoId
    await this.db.photos.put({
      id,
      blob: reduced,
      kind,
      bytes: reduced.size,
      createdAt: new Date().toISOString(),
    })
    return id
  }

  async restore(photo: {
    readonly id: PhotoId
    readonly blob: Blob
    readonly kind: StoredPhoto['kind']
    readonly createdAt: string
  }): Promise<'imported' | 'skipped'> {
    const existing = await this.db.photos.get(photo.id)
    if (existing) return 'skipped'
    // Sin pasar por `downscale`: ya se redujo cuando se guardó la primera vez, y
    // volver a codificarla en cada copia le iría quitando calidad.
    await this.db.photos.put({
      id: photo.id,
      blob: photo.blob,
      kind: photo.kind,
      bytes: photo.blob.size,
      createdAt: photo.createdAt,
    })
    return 'imported'
  }

  async get(id: PhotoId): Promise<Blob | null> {
    return (await this.db.photos.get(id))?.blob ?? null
  }

  async list(): Promise<readonly StoredPhoto[]> {
    const rows = await this.db.photos.toArray()
    return rows.map(({ id, kind, bytes, createdAt }) => ({ id, kind, bytes, createdAt }))
  }

  async remove(id: PhotoId): Promise<void> {
    await this.db.photos.delete(id)
  }

  async expired(now: Date, retentionDays: number): Promise<readonly PhotoId[]> {
    const cutoff = now.getTime() - retentionDays * 86_400_000
    const rows = await this.db.photos.toArray()
    return rows
      .filter((row) => row.kind !== 'reference' && Date.parse(row.createdAt) < cutoff)
      .map((row) => row.id)
  }

  async totalBytes(): Promise<number> {
    const rows = await this.db.photos.toArray()
    return rows.reduce((total, row) => total + row.bytes, 0)
  }
}
